// Explicit bundle manifest. Replaces the Vite-only `import.meta.glob` calls
// in registry.ts so the same registry runs unmodified on the server (Node).
//
// Adding a new map: drop a folder under src/maps/bundles/<dir>/ with map.json
// + behaviors/ + (optional) materials/, then add one entry below. Both Vite
// (browser) and Node (server) consume this same list — there is no per-side
// duplicate to keep in sync.

import type { BehaviorFactory } from '../Behavior';

import sandboxJson from './sandbox/map.json';
import boxingRingSunJson from './boxing-ring-sun/map.json';
import boxingRingMoonJson from './boxing-ring-moon/map.json';
import spaceshipInRingsJson from './spaceship-in-rings/map.json';
import sketchyAirshipJson from './sketchy-airship/map.json';
import sinkingShipJson from './sinking-ship/map.json';
import tutorialJson from './tutorial/map.json';

export type BehaviorLoader = () => Promise<{ default: BehaviorFactory }>;
export type MaterialLoader = () => Promise<{ default: unknown }>;

export interface BundleManifest {
  /** On-disk directory name under src/maps/bundles/. */
  dir: string;
  /** Pre-imported map.json content. Validated by the registry on first use. */
  json: unknown;
  /** Lazy behavior module loaders, keyed by module name (matches map.json's
   *  `behaviors[].module`). */
  behaviors: Record<string, BehaviorLoader>;
  /** Lazy material module loaders, keyed by module name. */
  materials: Record<string, MaterialLoader>;
}

export const BUNDLE_MANIFESTS: BundleManifest[] = [
  {
    dir: 'sandbox',
    json: sandboxJson,
    behaviors: {
      stairs: () => import('./sandbox/behaviors/stairs')
    },
    materials: {}
  },
  {
    dir: 'tutorial',
    json: tutorialJson,
    behaviors: {
      tutorialFlow: () => import('./tutorial/behaviors/tutorialFlow'),
      treadmill: () => import('./tutorial/behaviors/treadmill'),
      tutorialNpc: () => import('./tutorial/behaviors/tutorialNpc')
    },
    materials: {}
  },
  {
    dir: 'boxing-ring-sun',
    json: boxingRingSunJson,
    behaviors: {
      boxingRing: () => import('./boxing-ring-sun/behaviors/boxingRing'),
      solarSpurts: () => import('./boxing-ring-sun/behaviors/solarSpurts')
    },
    materials: {}
  },
  {
    dir: 'boxing-ring-moon',
    json: boxingRingMoonJson,
    behaviors: {
      boxingRing: () => import('./boxing-ring-moon/behaviors/boxingRing'),
      glassDome: () => import('./boxing-ring-moon/behaviors/glassDome'),
      moonPosts: () => import('./boxing-ring-moon/behaviors/moonPosts')
    },
    materials: {}
  },
  {
    dir: 'spaceship-in-rings',
    json: spaceshipInRingsJson,
    behaviors: {
      spaceshipTilt: () => import('./spaceship-in-rings/behaviors/spaceshipTilt'),
      asteroidField: () => import('./spaceship-in-rings/behaviors/asteroidField'),
      ionThrusters: () => import('./spaceship-in-rings/behaviors/ionThrusters')
    },
    materials: {}
  },
  {
    dir: 'sinking-ship',
    json: sinkingShipJson,
    behaviors: {
      sinkingShip: () => import('./sinking-ship/behaviors/sinkingShip'),
      oceanWater: () => import('./sinking-ship/behaviors/oceanWater')
    },
    materials: {}
  },
  {
    dir: 'sketchy-airship',
    json: sketchyAirshipJson,
    behaviors: {
      airshipFans: () => import('./sketchy-airship/behaviors/airshipFans'),
      airshipCrateSway: () => import('./sketchy-airship/behaviors/airshipCrateSway')
    },
    materials: {
      DeckPlateMaterial: () => import('./sketchy-airship/materials/DeckPlateMaterial'),
      HullFrameMaterial: () => import('./sketchy-airship/materials/HullFrameMaterial'),
      CrateWoodMaterial: () => import('./sketchy-airship/materials/CrateWoodMaterial'),
      BarrelWoodMaterial: () => import('./sketchy-airship/materials/BarrelWoodMaterial'),
      FanBladeMaterial: () => import('./sketchy-airship/materials/FanBladeMaterial'),
      FanHousingMaterial: () => import('./sketchy-airship/materials/FanHousingMaterial'),
      ChainMaterial: () => import('./sketchy-airship/materials/ChainMaterial'),
      HazardStripeMaterial: () => import('./sketchy-airship/materials/HazardStripeMaterial'),
      SpawnPadMaterial: () => import('./sketchy-airship/materials/SpawnPadMaterial'),
      YellowPipeMaterial: () => import('./sketchy-airship/materials/YellowPipeMaterial'),
      GratingMaterial: () => import('./sketchy-airship/materials/GratingMaterial'),
      SidePlatformMaterial: () => import('./sketchy-airship/materials/SidePlatformMaterial')
    }
  }
];
